import type {
  AnnulOperation,
  ConciseWorkspace,
  ConfigurationValidationError,
  ValidationErrorMessage,
} from '@festina/configuration-general-types/configuration'
import type {
  BranchKey,
  LayerKey,
  OperationLayerMap,
  WorkspaceKey,
} from '@festina-life/api'
import type { Except, Optional } from '@festina-life/core'
import { createRequiredContext } from '@festina-life/ui/libs'

import type { EnumDefinition } from '../uiModels/enumLiterals/types'
import type { ValidationErrorWithLocation } from '../uiModels/errors/types'
import type { IndexedGlMapOperations } from '../uiModels/glMapOperations/types'
import type {
  IndexedParameterDefinitionCreateOperations,
  IndexedParameterValueOperations,
} from '../uiModels/parameterOperations/types'
import type { IndexedReconciliationSpecOperations } from '../uiModels/reconciliationSpecOperations/types'
import type { IndexedRegisteredTenantCreateOperations } from '../uiModels/registeredTenantCreateOperations/types'
import type {
  IndexedTemplateOperations,
  ShadowedOperationsPerTemplateIdAndDate,
} from '../uiModels/templateOperations/types'
import type { OperationWithSourceInfo } from '../uiModels/types'
import type { ChangeOperation } from '../uiModels/workspace/types'

// Indexes configuration items by layer and item ID
export type ConfigurationItemsByLayer<T> = OperationLayerMap<Map<string, T>>
export type ConfigurationItemsByBlock<T> = Map<
  string,
  ConfigurationItemsByLayer<T>
>
export type ShadowedOperationsMap = Map<
  string,
  OperationLayerMap<ShadowedOperationsPerTemplateIdAndDate>
>

export type OperationsContext = {
  workspaceKey: Optional<WorkspaceKey>
  branchKey: Optional<BranchKey>
  workspace: Optional<ConciseWorkspace>
  onAddOrUpdateOperation: (props: {
    operation: ChangeOperation
    layer: LayerKey
    block: string
    shouldOpenUnifiedBranchWorkspace: boolean
  }) => Promise<WorkspaceKey>
  onRemoveOperation: (operation: ChangeOperation) => Promise<void>

  // region: Indexed configuration items
  templateOperations: ConfigurationItemsByBlock<IndexedTemplateOperations>
  parameterDefinitionCreateOperations: ConfigurationItemsByBlock<IndexedParameterDefinitionCreateOperations>
  parameterValueEntityOperations: ConfigurationItemsByBlock<IndexedParameterValueOperations>
  configuredEnums: ConfigurationItemsByBlock<EnumDefinition>
  registeredTenantCreateOperations: ConfigurationItemsByBlock<IndexedRegisteredTenantCreateOperations>
  glMapOperations: ConfigurationItemsByBlock<IndexedGlMapOperations>
  reconciliationSpecCreateOperations: ConfigurationItemsByBlock<IndexedReconciliationSpecOperations>
  /** All annul operations (committed and draft) indexed by their `operationKey`. */
  allAnnulOperationsByKey: Map<string, OperationWithSourceInfo<AnnulOperation>>
  // endregion: Indexed configuration items

  workspaceShadowedTemplateOperations: ShadowedOperationsMap
  branchShadowedTemplateOperations: ShadowedOperationsMap
  /**
   * Whether the operations are being loaded for the first time, with nothing to show
   * yet. Background refreshes, such as the one following an approval, keep this false
   * so that the scenes reading it are not torn down and remounted.
   */
  isLoading: boolean
  isLoadingErrors: boolean
  errors: ValidationErrorWithLocation<ConfigurationValidationError>[]
  workspaceValidationErrors: Map<
    string,
    OperationLayerMap<ValidationErrorMessage | Error>
  >
  branchValidationErrors: Map<
    string,
    OperationLayerMap<ValidationErrorMessage | Error>
  >
}

const [useOperationsContext, Provider] =
  createRequiredContext<OperationsContext>('Operations')

const useRequiredWorkspaceOperationsContext = (): Except<
  OperationsContext,
  'workspaceKey' | 'workspace' | 'branchKey'
> & {
  workspaceKey: WorkspaceKey
  workspace: ConciseWorkspace
  branchKey: BranchKey
} => {
  const { workspaceKey, workspace, branchKey, ...rest } = useOperationsContext()

  if (!workspaceKey || !workspace) {
    throw new Error(
      'Could not find a workspace. Please make sure you are in a workspace context',
    )
  }

  if (!branchKey) {
    throw new Error(
      'Could not find a branch. Please make sure a branch is open',
    )
  }

  return { ...rest, workspaceKey, workspace, branchKey }
}

const useRequiredBranchOperationsContext = (): Except<
  OperationsContext,
  'branchKey'
> & {
  branchKey: BranchKey
} => {
  const { branchKey, ...rest } = useOperationsContext()

  if (!branchKey) {
    throw new Error(
      'Could not find a branch. Please make sure a branch is open',
    )
  }

  return { ...rest, branchKey }
}

export {
  Provider,
  useOperationsContext,
  useRequiredBranchOperationsContext,
  useRequiredWorkspaceOperationsContext,
}
